import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import InstallCard from '../components/InstallCard';
import { useI18n, t as tr, optLabel } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { fmtDay, fmtMoney, statusTag } from '../lib/format';
import { ledgerTotals, paidTotal, balanceOf } from '../lib/ledger';
import LedgerCard from '../components/LedgerCard';
import CompliancePanel, { ComplianceBanner } from '../components/CompliancePanel';
import MessageButton from '../components/MessageButton';
import TechSettlement from '../components/TechSettlement';
import RecordList from '../components/RecordList';
import { localized } from '../lib/translated';
import { docWho, docAbout } from '../lib/docSummary';
import {
  IconHome, IconJobs, IconEstimates, IconInvoices, IconPayments, IconMore, IconChevron,
} from '../components/PortalIcons';
import VendorOverview from './VendorOverview';
import JobOffers from '../components/JobOffers';
import VendorSetupNote from '../components/VendorSetupNote';
import PayoutDetails from '../components/PayoutDetails';
import OnlinePayments from '../components/OnlinePayments';
import { PlatformFeeExplainer, PlatformFeeAgreement, StripeHandoffNote } from '../components/PlatformFeeTerms';
import W9Form from '../components/W9Form';
import { TAXONOMY, YEARS_1_100 } from '../lib/trades';
import ThemeToggle from '../components/ThemeToggle';
import Wordmark from '../components/Wordmark';

// Expertise taxonomy: main category → sub-services. Every category and skill
// from the original Google Form is preserved (so past applications still read
// correctly), organized into a category tree with a few added sub-services.
// VALUES stay in English (that's what saves to the database) — labels translate.
// Re-exported so the admin board keeps its single import.
export { TAXONOMY };

// Yes / No, in the wizard's own control rather than the old pill buttons, so a
// question reads the same as the either-or rows beside it.
const YesNo = ({ label, required, value, onChange }) => (
  <div className="qps-input-block">
    <label>{label} {required && <span className="qps-required">*</span>}</label>
    <div className="qps-choice-row">
      {['Yes', 'No'].map(opt => (
        <button key={opt} type="button"
          className={`qps-choice${value === opt ? ' on' : ''}`}
          onClick={() => onChange(opt)}>{optLabel(opt)}</button>
      ))}
    </div>
  </div>
);

// ── Trades, and the jobs inside them ───────────────────────────────────────
//
// Pulled out of the onboarding wizard so the profile can show the same picker.
// It IS the routing table — a job reaches a technician because of what was
// ticked here — and two pickers drifting apart would mean the screen a tech
// edits and the screen QPS assigns from disagree about what they do.
//
// It owns only which trade is open and what is typed in its search box. The
// trades and ticks themselves live in the caller's form.
function TradePicker({ value: f, onChange: set }) {
  // One checklist open at a time: 22 open lists is a wall.
  const [openTrade, setOpenTrade] = useState(null);
  // Typing to narrow the ticks inside the open trade. Handyman & General
  // Repairs carries 36 of them, and scrolling 36 checkboxes on a phone to find
  // "Fence repair" is how a tech gives up and ticks Select all.
  const [skillQ, setSkillQ] = useState('');

  const addTrade = (cat) => {
    if (cat && !f.expertise.includes(cat)) {
      set({ expertise: [...f.expertise, cat] });
      setOpenTrade(cat);   // open the checklist straight away — that is the point
      setSkillQ('');
    }
  };
  const removeTrade = (cat) => {
    const subs = TAXONOMY.find(x => x.cat === cat)?.subs || [];
    const kept = f.expertise.filter(c => c !== cat);
    // A handful of skills sit under two trades — cabinet installation is on
    // both the cabinetry list and the handyman list. Dropping one trade must
    // not quietly untick a skill the other trade still covers.
    const stillCovered = new Set(
      kept.flatMap(c => TAXONOMY.find(x => x.cat === c)?.subs || [])
    );
    set({
      expertise: kept,
      skills: f.skills.filter(sk => !subs.includes(sk) || stillCovered.has(sk)),
    });
    if (openTrade === cat) setOpenTrade(null);
  };
  const toggleSkill = (sk) => set({ skills: f.skills.includes(sk) ? f.skills.filter(x => x !== sk) : [...f.skills, sk] });
  const allInTrade = (cat, on) => {
    const subs = TAXONOMY.find(x => x.cat === cat)?.subs || [];
    set({ skills: on ? [...new Set([...f.skills, ...subs])] : f.skills.filter(sk => !subs.includes(sk)) });
  };
  const pickedIn = (cat) => (TAXONOMY.find(x => x.cat === cat)?.subs || []).filter(sk => f.skills.includes(sk)).length;

  return (
              <div className="qps-input-block">
                  <label htmlFor="tradePick">What do you do? <span className="qps-required">*</span></label>
                  <p className="qps-input-hint">
                    Pick a trade, then tick the jobs inside it you actually take. This is
                    how work finds you — a job you have not ticked will never be sent to you.
                  </p>
                  <select id="tradePick" value="" onChange={e => addTrade(e.target.value)}>
                    <option value="">Add a trade…</option>
                    {TAXONOMY.filter(x => !f.expertise.includes(x.cat)).map(x => (
                      <option key={x.cat} value={x.cat}>{x.cat}</option>
                    ))}
                  </select>
  
                  {f.expertise.length === 0 && (
                    <div className="qps-card-empty tight" style={{ marginTop: 12 }}>
                      No trades picked yet. Most vendors pick two or three.
                    </div>
                  )}
  
                  {f.expertise.map(cat => {
                    const subs = TAXONOMY.find(x => x.cat === cat)?.subs || [];
                    const open = openTrade === cat;
                    const n = pickedIn(cat);
                    return (
                      <div key={cat} className={`qps-trade-card${open ? ' open' : ''}`}>
                        <div className="qps-trade-head">
                          <button type="button" className="qps-trade-toggle" onClick={() => { setSkillQ(''); setOpenTrade(open ? null : cat); }} aria-expanded={open}>
                            <span className="qps-trade-chev" aria-hidden="true">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
                            </span>
                            <b>{cat}</b>
                            <i>{subs.length === 0 ? 'not on the list' : n > 0 ? `${n} of ${subs.length} selected` : `tick what you take`}</i>
                          </button>
                          <button type="button" className="qps-trade-x" onClick={() => removeTrade(cat)} aria-label={`Remove ${cat}`}>×</button>
                        </div>
  
                        {open && (
                          <div className="qps-trade-body">
                            {/* A trade whose name no longer matches the list —
                                saved before it was spelled this way, or typed by
                                hand. Nothing to tick, so say that rather than
                                showing an empty box and letting the tech decide
                                the picker is broken. */}
                            {subs.length === 0 ? (
                              <p className="qps-trade-gone">
                                “{cat}” is not one of the trades work is routed by any more.
                                Remove it with the × above and add the closest one from the list —
                                your ticks under it are kept on file either way.
                              </p>
                            ) : (
                              <>
                                <div className="qps-trade-actions">
                                  <button type="button" className="qps-btn-secondary" onClick={() => allInTrade(cat, true)}>Select all</button>
                                  <button type="button" className="qps-btn-secondary" onClick={() => allInTrade(cat, false)}>Clear</button>
                                </div>
                                {subs.length > 12 && (
                                  <input
                                    type="search"
                                    className="qps-trade-find"
                                    value={skillQ}
                                    onChange={e => setSkillQ(e.target.value)}
                                    placeholder={`Find a job in ${subs.length}…`}
                                    aria-label={`Search ${cat} jobs`}
                                  />
                                )}
                                <div className="qps-check-grid">
                                  {subs
                                    .filter(sk => !skillQ.trim() || sk.toLowerCase().includes(skillQ.trim().toLowerCase()))
                                    .map(sk => (
                                      <label key={sk} className="qps-check">
                                        <input type="checkbox" checked={f.skills.includes(sk)} onChange={() => toggleSkill(sk)} />
                                        <span>{sk}</span>
                                      </label>
                                    ))}
                                </div>
                                {skillQ.trim() && subs.filter(sk => sk.toLowerCase().includes(skillQ.trim().toLowerCase())).length === 0 && (
                                  <p className="qps-trade-gone">
                                    Nothing in {cat} matches “{skillQ.trim()}”. Anything genuinely missing
                                    goes in the box under the trades.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
  
                  <input type="text" value={f.expertiseOther} style={{ marginTop: 12 }}
                    onChange={e => set({ expertiseOther: e.target.value })}
                    placeholder="Something else you do that is not listed (optional)" />
                </div>
  );
}

// Partnership application shown until the tech completes it (once per account).
// ═══════════════════════════════════════════════════════════════════════════
//  VENDOR ONBOARDING
//
//  Three steps: the business, the trades, the paperwork. One kind of thing per
//  step, and no way past a step until that step is answered.
//
//  WHY THE TRADES GET A STEP OF THEIR OWN. The picker is a dropdown of 22
//  trades, each opening a checklist of the specific jobs inside it — 254 of
//  them. That runs longer on its own than the other two steps together, and it
//  is the single most important thing on the form: it IS the routing table. A
//  job reaches a vendor because of what was ticked here, so a trade nobody
//  ticked is work that never gets assigned.
// ═══════════════════════════════════════════════════════════════════════════

const STEPS = [
  { key: 'business',    label: 'Business' },
  { key: 'trades',      label: 'Trades' },
  { key: 'credentials', label: 'Paperwork' },
  // Set the bank up on day one, while they are sitting down and have their
  // details to hand. A technician who leaves this for later leaves it forever,
  // and then their first invoice has no way to be paid.
  { key: 'payments',    label: 'Get paid' },
];

const US_STATES = ['UT', 'AZ', 'CO', 'ID', 'NV', 'NM', 'WY', 'AL','AK','AR','CA','CT','DC','DE','FL','GA','HI','IA','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','VA','VT','WA','WI','WV'];

// One named document: a drag-and-drop zone that remembers what it is holding.
// Four of these beat one bag of five files — a reviewer should not have to open
// every attachment to find out which one is the insurance certificate.
function DocSlot({ id, label, hint, required, file, onPick, onClear }) {
  const [dragging, setDragging] = useState(false);
  const input = useRef(null);

  const take = (list) => {
    const picked = Array.from(list || []).find(f =>
      /^image\//.test(f.type) || f.type === 'application/pdf'
    );
    if (picked) onPick(picked);
  };

  return (
    <div className="qps-input-block">
      <label htmlFor={id}>
        {label} {required ? <span className="qps-required">*</span> : <span className="qps-optional">(optional)</span>}
      </label>
      {hint && <p className="qps-input-hint">{hint}</p>}

      {file ? (
        <div className="qps-doc-held">
          <span className="qps-doc-tick" aria-hidden="true">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5" /></svg>
          </span>
          <span className="qps-doc-name">{file.name}</span>
          <button type="button" className="qps-doc-clear" onClick={onClear} aria-label={`Remove ${file.name}`}>Remove</button>
        </div>
      ) : (
        <div
          className={`qps-upload-vault${dragging ? ' dragging' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files); }}
        >
          <input
            ref={input}
            id={id}
            type="file"
            className="qps-hidden-file"
            accept="image/*,.pdf"
            aria-label={label}
            onChange={e => { take(e.target.files); e.target.value = ''; }}
          />
          <div className="qps-upload-ui">
            <span className="qps-upload-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
                <path d="M3.5 15v3.5A1.5 1.5 0 005 20h14a1.5 1.5 0 001.5-1.5V15" />
              </svg>
            </span>
            <span className="qps-upload-text">Drag and drop, or choose a file</span>
            <span className="qps-upload-sub">PDF, JPG or PNG</span>
          </div>
        </div>
      )}
    </div>
  );
}

// `editing` is the same wizard, reopened on a profile that already exists.
// One component rather than two, because a second "edit your details" form is
// how the edit screen ends up asking for different things than the one that
// signed them up.
function VendorApplication({ editing = false, onClose }) {
  const {
    vendorProfile, vendorAppForm, setVendorAppForm, vendorDocs, setVendorDocs,
    isSubmittingApp, handleSubmitVendorApp, currentUser,
  } = useApp();
  const { t } = useI18n();
  const f = vendorAppForm;
  const set = (patch) => setVendorAppForm(p => ({ ...p, ...patch }));
  const setDoc = (field, file) => setVendorDocs(p => ({ ...p, [field]: file }));

  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);

  // The platform fee and chargeback terms, agreed here rather than weeks later,
  // because the Stripe account is created the moment they tap the button.
  const [agreeFees, setAgreeFees] = useState(false);

  // Registration already took a name and a phone. Seeding them means step one
  // is a tech confirming two things rather than typing them again. Runs once —
  // re-seeding on every render would fight anything they type.
  useEffect(() => {
    set({
      businessName: vendorAppForm.businessName || vendorProfile?.businessName || currentUser?.name || '',
      phone: vendorAppForm.phone || vendorProfile?.phone || currentUser?.phone || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // What each step still needs, named per step so Continue can refuse with a
  // reason rather than sitting there greyed out saying nothing.
  const missing = [
    [
      !f.businessName && 'a business name',
      !f.address && 'a street address',
      !f.city && 'a city',
      !f.state && 'a state',
      !f.zip && 'a ZIP code',
      !f.phone && 'a phone number',
    ],
    [
      f.expertise.length === 0 && 'at least one trade',
      f.expertise.length > 0 && f.skills.length === 0 && 'at least one job under a trade',
      !f.yearsExperience && 'years of experience',
      f.hasCrew === 'Yes' && !f.crewSize && 'how many people are on your crew',
    ],
    [
      !f.hasQualifier && 'whether the business has a licensed qualifier',
    ],
    // Only the Stripe button needs this. Taking the door out ignores it, so
    // getting paid online stays optional while the agreement stays required
    // for anyone who does set it up.
    [
      !agreeFees && 'your agreement to the QPS Hub fee',
    ],
  ].map(list => list.filter(Boolean));

  const blocked = missing[step];
  const last = step === STEPS.length - 1;

  const go = (n) => { setTouched(false); setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const next = () => { if (blocked.length > 0) { setTouched(true); return; } go(step + 1); };
  // Save the application, then hand them straight to Stripe. The save has to
  // happen first: Stripe's onboarding is on Stripe's own site, so a form still
  // sitting in the browser would die the moment they left.
  const finish = (connectStripe) => {
    // Setting payments up means agreeing to the fee first. Leaving them for
    // later means agreeing to nothing, so nothing is checked.
    if (connectStripe && blocked.length > 0) { setTouched(true); return; }
    handleSubmitVendorApp({ connectStripe });
  };

  return (
    <div className="qps-onboarding">
      <main className="qps-onboarding-container">

        {/* A way back out. Without it a technician who taps in from the portal
            is held here until he finishes, which is the wall this screen
            stopped being. His answers are kept either way — the wizard saves
            at each step. */}
        {onClose && (
          <button type="button" className="qps-wizard-back" onClick={onClose}>
            ← Back to my portal
          </button>
        )}

        <div className="qps-wizard-meta">
          <span className="qps-step-count">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
          <h1 className="qps-title">{t('app.title')}</h1>
          <p className="qps-wizard-sub">{t('app.sub')}</p>
          <div className="qps-progress-bar" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={STEPS.length} aria-label={`Step ${step + 1} of ${STEPS.length}`}>
            <div className="qps-progress-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <div className="qps-form-stack">

          {/* ── 1. Business ──────────────────────────────────────────── */}
          {step === 0 && (
            <fieldset className="qps-form-group">
              <legend className="qps-section-heading">Business information</legend>

              <div className="qps-input-block">
                <label htmlFor="bizName">Business name <span className="qps-required">*</span></label>
                <p className="qps-input-hint">What goes on an invoice. Your own name is fine if you work alone.</p>
                <input id="bizName" type="text" value={f.businessName} onChange={e => set({ businessName: e.target.value })} placeholder="Ortiz Drywall LLC" />
              </div>

              <div className="qps-input-block">
                <label htmlFor="bizStreet">Street address <span className="qps-required">*</span></label>
                <input id="bizStreet" type="text" value={f.address} onChange={e => set({ address: e.target.value })} placeholder="1420 West Main Street" autoComplete="address-line1" />
              </div>

              <div className="qps-field-row">
                <div className="qps-input-block">
                  <label htmlFor="bizCity">City <span className="qps-required">*</span></label>
                  <input id="bizCity" type="text" value={f.city} onChange={e => set({ city: e.target.value })} placeholder="Lehi" autoComplete="address-level2" />
                </div>
                <div className="qps-input-block qps-field-sm">
                  <label htmlFor="bizState">State <span className="qps-required">*</span></label>
                  <select id="bizState" value={f.state} onChange={e => set({ state: e.target.value })} autoComplete="address-level1">
                    {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div className="qps-input-block qps-field-sm">
                  <label htmlFor="bizZip">ZIP <span className="qps-required">*</span></label>
                  <input id="bizZip" type="text" inputMode="numeric" maxLength={10} value={f.zip} onChange={e => set({ zip: e.target.value })} placeholder="84043" autoComplete="postal-code" />
                </div>
              </div>

              <div className="qps-input-block">
                <label htmlFor="bizPhone">Phone number <span className="qps-required">*</span></label>
                <p className="qps-input-hint">The number we text job details to.</p>
                <input id="bizPhone" type="tel" value={f.phone} onChange={e => set({ phone: e.target.value })} placeholder="(801) 555-0100" autoComplete="tel" />
              </div>
            </fieldset>
          )}

          {/* ── 2. Trades ────────────────────────────────────────────── */}
          {step === 1 && (
            <fieldset className="qps-form-group">
              <legend className="qps-section-heading">Trades and experience</legend>

              <TradePicker value={f} onChange={set} />

              <div className="qps-input-block">
                <label htmlFor="techExp">Years of experience <span className="qps-required">*</span></label>
                <select id="techExp" value={f.yearsExperience} onChange={e => set({ yearsExperience: e.target.value })}>
                  <option value="">Select…</option>
                  {YEARS_1_100.map(y => <option key={y} value={y}>{y} {y === '1' ? 'year' : 'years'}</option>)}
                </select>
              </div>

              <YesNo label="Do you have your own tools?" value={f.hasTools} onChange={v => set({ hasTools: v })} />

              <YesNo label="Do you have a crew?" value={f.hasCrew} onChange={v => set({ hasCrew: v, crewSize: v === 'No' ? '' : f.crewSize })} />
              {f.hasCrew === 'Yes' && (
                <div className="qps-input-block">
                  <label htmlFor="crewSize">How many people, including you? <span className="qps-required">*</span></label>
                  <select id="crewSize" value={f.crewSize} onChange={e => set({ crewSize: e.target.value })}>
                    <option value="">Select…</option>
                    {YEARS_1_100.map(n => <option key={n} value={n}>{n} {n === '1' ? 'person' : 'people'}</option>)}
                  </select>
                </div>
              )}
            </fieldset>
          )}

          {/* ── 3. Paperwork ─────────────────────────────────────────── */}
          {step === 2 && (
            <fieldset className="qps-form-group">
              <legend className="qps-section-heading">Business paperwork</legend>
              <p className="qps-input-hint" style={{ marginBottom: 18 }}>
                Every document can be added later from My Documents. Nothing is
                routed to you until they are on file, so sooner is better, but an
                application without them still gets reviewed.
              </p>

              <div className="qps-input-block">
                <label htmlFor="ein">EIN <span className="qps-optional">(or SSN if you are a sole proprietor)</span></label>
                <input id="ein" type="text" value={f.einNumber} onChange={e => set({ einNumber: e.target.value })} placeholder="12-3456789" />
              </div>
              <DocSlot id="doc-w9" label="W-9" hint="Signed, current year."
                file={vendorDocs.w9} onPick={file => setDoc('w9', file)} onClear={() => setDoc('w9', null)} />

              <div className="qps-input-block">
                <label htmlFor="policy">Insurance policy number</label>
                <input id="policy" type="text" value={f.insurancePolicyNumber} onChange={e => set({ insurancePolicyNumber: e.target.value })} placeholder="GL-00123456" />
              </div>
              <DocSlot id="doc-ins" label="Certificate of insurance" hint="General liability, showing the expiry date."
                file={vendorDocs.insurance} onPick={file => setDoc('insurance', file)} onClear={() => setDoc('insurance', null)} />

              {/* A waiver is a real document, not the absence of one. Utah lets an
                  owner-operator file one, and it has to be on file either way. */}
              <div className="qps-input-block">
                <label>Workers compensation</label>
                <div className="qps-choice-row">
                  {[['policy', 'I carry a policy'], ['waiver', 'I have a waiver']].map(([v, lbl]) => (
                    <button key={v} type="button"
                      className={`qps-choice${f.workersCompType === v ? ' on' : ''}`}
                      onClick={() => set({ workersCompType: f.workersCompType === v ? '' : v })}>{lbl}</button>
                  ))}
                </div>
              </div>
              {f.workersCompType && (
                <DocSlot id="doc-wc" label={f.workersCompType === 'waiver' ? 'Workers comp waiver' : 'Workers comp policy'}
                  file={vendorDocs.workersComp} onPick={file => setDoc('workersComp', file)} onClear={() => setDoc('workersComp', null)} />
              )}

              <YesNo label="Does the business have a qualifier with a contractor licence?" required
                value={f.hasQualifier} onChange={v => set({ hasQualifier: v })} />

              {f.hasQualifier === 'Yes' && (
                <>
                  <div className="qps-field-row">
                    <div className="qps-input-block">
                      <label htmlFor="qname">Qualifier name</label>
                      <input id="qname" type="text" value={f.qualifierName} onChange={e => set({ qualifierName: e.target.value })} placeholder="Who the licence is held by" />
                    </div>
                    <div className="qps-input-block">
                      <label htmlFor="qnum">Licence number</label>
                      <input id="qnum" type="text" value={f.qualifierLicenseNumber} onChange={e => set({ qualifierLicenseNumber: e.target.value })} placeholder="We check this against Utah DOPL" />
                    </div>
                  </div>
                  <DocSlot id="doc-lic" label="Contractor licence"
                    file={vendorDocs.contractorLicense} onPick={file => setDoc('contractorLicense', file)} onClear={() => setDoc('contractorLicense', null)} />
                </>
              )}
            </fieldset>
          )}

          {/* ── 4. Get paid ──────────────────────────────────────────── */}
          {step === 3 && (
            <fieldset className="qps-form-group">
              <legend className="qps-section-heading">Getting paid</legend>
              <p className="qps-input-hint" style={{ marginBottom: 10 }}>
                <strong>Writing estimates and invoices for your own customers is free.</strong> It always will be,
                and this step is not needed for it. Skip it and everything in the app still works.
              </p>
              <p className="qps-input-hint" style={{ marginBottom: 14 }}>
                Set it up to let your own customers pay you online by bank transfer or card, and so QPS Hub can
                pay you for any work we send you. The money goes to your bank account. No monthly fee, and
                nothing to pay unless you get paid.
              </p>

              <PlatformFeeExplainer />
              <PlatformFeeAgreement checked={agreeFees} onChange={setAgreeFees} id="agree-fees-signup" />

              <StripeHandoffNote />

              {/* The door out. Without it a technician standing in a driveway
                  without his routing number cannot join at all, and we never
                  find out he existed. */}
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button type="button" className="qps-btn-back" disabled={isSubmittingApp} onClick={() => finish(false)}>
                  I'll set up payments later
                </button>
                <p className="qps-input-hint" style={{ marginTop: 6 }}>
                  Your application is sent either way, and payments can be set up any time from the Payments tab.
                </p>
              </div>
            </fieldset>
          )}

          {touched && blocked.length > 0 && (
            <div className="qps-wizard-error" role="alert">Still needed: {blocked.join(', ')}.</div>
          )}

          <div className={`qps-action-tray${last ? ' flow' : ''}`}>
            {step > 0 && <button type="button" className="qps-btn-back" onClick={() => go(step - 1)}>Back</button>}
            {last ? (
              <button type="button" className="qps-btn-primary" disabled={isSubmittingApp} onClick={() => finish(true)}>
                {isSubmittingApp ? t('req.submitting') : 'Finish and set up payments'}
              </button>
            ) : (
              <button type="button" className="qps-btn-primary" onClick={next}>Continue</button>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}

// ── The technician's own profile ────────────────────────────────────────────
//
// Filled in HERE. It used to be a read-back with an Edit button that threw you
// into the onboarding wizard — a second screen, three steps, and a trip back.
// Julian's words: "I just want it all done here."
//
// So every field is live. It edits `vendorAppForm`, the same object the wizard
// fills, and saves through the same endpoint — one set of fields and one set
// of rules, rather than an edit screen that slowly asks different questions
// than the one that signs people up.
//
// Nothing saves until Save is pressed. A profile that wrote on every keystroke
// would be a routing table rewritten mid-sentence, and a half-typed trade is a
// job sent to the wrong person.
function VendorProfile() {
  const {
    vendorProfile, currentUser, setVendorView,
    vendorAppForm, setVendorAppForm, seedVendorForm,
    saveVendorProfile, isSubmittingApp,
    handleVendorLogo, handleRemoveVendorLogo, isSavingLogo,
  } = useApp();
  const { t } = useI18n();
  const v = vendorProfile || {};
  const p = v.profile || {};
  const f = vendorAppForm;

  // Load what is on file into the form, and again whenever the saved record
  // changes underneath — a logo upload returns a fresh vendor. Never while
  // there are unsaved edits on the screen.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) seedVendorForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorProfile]);

  const set = (patch) => { setDirty(true); setVendorAppForm(prev => ({ ...prev, ...patch })); };

  const save = async () => {
    const ok = await saveVendorProfile();
    if (ok) setDirty(false);
  };

  const Field = ({ label, id, hint, children }) => (
    <div className="qps-input-block">
      <label htmlFor={id}>{label}</label>
      {hint && <p className="qps-input-hint">{hint}</p>}
      {children}
    </div>
  );

  return (
    <>
      <div className="tp-head">
        <div className="tp-head-row">
          <button className="tp-act" onClick={() => setVendorView('more')} aria-label={t('nav.back')}
            style={{ transform: 'scaleX(-1)' }}><IconChevron size={20} /></button>
          <div className="tp-title">{t('tp.profile')}</div>
          <div style={{ width: 34 }} />
        </div>
      </div>

      <div className="vp">
        <div className="vp-id">
          {/* The picture saves on its own, the moment one is picked. It is a
              file rather than a field, and holding it behind the same Save as
              the text would be a surprise. */}
          <label className={`vp-avatar${p.logoUrl ? ' has-img' : ''}`}
            title="Change your picture"
            style={p.logoUrl ? { backgroundImage: `url(${p.logoUrl})` } : undefined}>
            {!p.logoUrl && initials(v.businessName || currentUser?.name)}
            <input type="file" accept="image/*" hidden disabled={isSavingLogo}
              onChange={e => { handleVendorLogo(e.target.files[0]); e.target.value = ''; }} />
            <span className="vp-avatar-cam" aria-hidden="true">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5h3l1.6-2.4h8.8L18 8.5h3v11H3z" /><circle cx="12" cy="13.5" r="3.4" /></svg>
            </span>
          </label>
          <div>
            <div className="vp-name">{v.businessName || currentUser?.name}</div>
            <div className="vp-sub">{(f.expertise || []).join(' · ') || 'No trades listed yet'}</div>
            <div className="vp-pic-acts">
              <label>
                {isSavingLogo ? 'Uploading…' : (p.logoUrl ? 'Change picture' : 'Add a logo or photo')}
                <input type="file" accept="image/*" hidden disabled={isSavingLogo}
                  onChange={e => { handleVendorLogo(e.target.files[0]); e.target.value = ''; }} />
              </label>
              {p.logoUrl && (
                <button type="button" onClick={handleRemoveVendorLogo} disabled={isSavingLogo}>Remove</button>
              )}
            </div>
          </div>
        </div>

        <section className="vp-card edit">
          <h3>Business</h3>
          <Field label="Business name" id="vpName" hint="What goes on an invoice. Your own name is fine if you work alone.">
            <input id="vpName" type="text" value={f.businessName}
              onChange={e => set({ businessName: e.target.value })} placeholder="Ortiz Drywall LLC" />
          </Field>
          <Field label="Street address" id="vpStreet">
            <input id="vpStreet" type="text" value={f.address} autoComplete="address-line1"
              onChange={e => set({ address: e.target.value })} placeholder="1420 West Main Street" />
          </Field>
          <div className="qps-field-row">
            <Field label="City" id="vpCity">
              <input id="vpCity" type="text" value={f.city} autoComplete="address-level2"
                onChange={e => set({ city: e.target.value })} placeholder="Lehi" />
            </Field>
            <div className="qps-input-block qps-field-sm">
              <label htmlFor="vpState">State</label>
              <select id="vpState" value={f.state} onChange={e => set({ state: e.target.value })} autoComplete="address-level1">
                {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div className="qps-input-block qps-field-sm">
              <label htmlFor="vpZip">ZIP</label>
              <input id="vpZip" type="text" inputMode="numeric" maxLength={10} value={f.zip}
                autoComplete="postal-code" onChange={e => set({ zip: e.target.value })} placeholder="84043" />
            </div>
          </div>
          <Field label="Phone number" id="vpPhone" hint="The number we text job details to.">
            <input id="vpPhone" type="tel" value={f.phone} autoComplete="tel"
              onChange={e => set({ phone: e.target.value })} placeholder="(801) 555-0100" />
          </Field>
          {/* Read-only: it is the address you sign in with, and changing it is
              an account change rather than a profile one. */}
          <div className="vp-row"><span className="vp-row-l">Email</span><span className="vp-row-v">{v.email || currentUser?.email}</span></div>
        </section>

        <section className="vp-card edit">
          <h3>Trades</h3>
          <TradePicker value={f} onChange={set} />
        </section>

        <section className="vp-card edit">
          <h3>Experience</h3>
          <Field label="Years in the trade" id="vpYears">
            <select id="vpYears" value={f.yearsExperience} onChange={e => set({ yearsExperience: e.target.value })}>
              <option value="">Select…</option>
              {/* A value saved before this was a dropdown — "10+ years
                  (Specialist)" and the like — is kept as its own option rather
                  than silently swapped for a number nobody chose. */}
              {f.yearsExperience && !YEARS_1_100.includes(f.yearsExperience) && (
                <option value={f.yearsExperience}>{f.yearsExperience}</option>
              )}
              {YEARS_1_100.map(y => <option key={y} value={y}>{y} {y === '1' ? 'year' : 'years'}</option>)}
            </select>
          </Field>
          <YesNo label="Do you have your own tools?" value={f.hasTools} onChange={val => set({ hasTools: val })} />
          <YesNo label="Do you have a crew?" value={f.hasCrew}
            onChange={val => set({ hasCrew: val, crewSize: val === 'No' ? '' : f.crewSize })} />
          {f.hasCrew === 'Yes' && (
            <Field label="How many people, including you?" id="vpCrew">
              <select id="vpCrew" value={f.crewSize} onChange={e => set({ crewSize: e.target.value })}>
                <option value="">Select…</option>
                {YEARS_1_100.map(n => <option key={n} value={n}>{n} {n === '1' ? 'person' : 'people'}</option>)}
              </select>
            </Field>
          )}
        </section>

        <section className="vp-card edit">
          <h3>Paperwork</h3>
          <Field label="EIN" id="vpEin" hint="Leave blank if you file under your Social Security number.">
            <input id="vpEin" type="text" value={f.einNumber}
              onChange={e => set({ einNumber: e.target.value })} placeholder="87-1234567" />
          </Field>
          <Field label="Insurance policy number" id="vpIns">
            <input id="vpIns" type="text" value={f.insurancePolicyNumber}
              onChange={e => set({ insurancePolicyNumber: e.target.value })} />
          </Field>
          <Field label="Workers comp" id="vpWc">
            <select id="vpWc" value={f.workersCompType} onChange={e => set({ workersCompType: e.target.value })}>
              <option value="">Select…</option>
              <option value="policy">I carry a policy</option>
              <option value="waiver">Exempt — I filed a waiver</option>
            </select>
          </Field>
          <YesNo label="Does the business have a licensed qualifier?" value={f.hasQualifier}
            onChange={val => set({ hasQualifier: val })} />
          {f.hasQualifier === 'Yes' && (
            <>
              <Field label="Qualifier name" id="vpQn">
                <input id="vpQn" type="text" value={f.qualifierName}
                  onChange={e => set({ qualifierName: e.target.value })} placeholder="Who the licence is held by" />
              </Field>
              <Field label="Licence number" id="vpQl">
                <input id="vpQl" type="text" value={f.qualifierLicenseNumber}
                  onChange={e => set({ qualifierLicenseNumber: e.target.value })} placeholder="We check this against Utah DOPL" />
              </Field>
            </>
          )}
          {/* What QPS found when it looked the licence up. Read-only on purpose:
              a technician attesting to their own licence is not verification,
              it is the claim being verified. The point of showing it here is
              that nobody is surprised by a number QPS has on file. */}
          {(() => {
            const lv = v.licenseVerification || {};
            const st = v.licenseState || {};
            const passed = ['active', 'not-required'].includes(lv.result);
            const tone = !lv.checkedAt ? 'slate' : !passed ? 'red' : st.stale ? 'amber' : 'green';
            const words = {
              active: 'Active and in good standing',
              expired: 'Expired',
              suspended: 'Suspended or revoked',
              'not-found': 'No licence found under that number',
              'name-mismatch': 'Found under a different name',
              'not-required': 'No licence needed for the work you take',
            };
            return (
              <div className="lic-box" data-tone={tone}>
                <div className="lic-head"><span className="lic-title">Your licence, checked by QPS</span></div>
                {!lv.checkedAt ? (
                  <div className="lic-line">QPS has not looked yet. Nothing for you to do.</div>
                ) : (
                  <>
                    <div className="lic-line strong">{words[lv.result] || lv.result}</div>
                    <div className="lic-line">
                      {[lv.licenseNumber && `#${lv.licenseNumber}`, lv.licenseType].filter(Boolean).join(' \u00b7 ')}
                      {lv.licenseExpiresAt && ` \u00b7 expires ${fmtDay(lv.licenseExpiresAt)}`}
                    </div>
                    <div className="lic-line">Looked up {fmtDay(lv.checkedAt)}.</div>
                    {st.state === 'licence-expired' && (
                      <div className="lic-line warn">Your licence has expired. Renew it with DOPL and tell QPS.</div>
                    )}
                    {!passed && lv.result !== 'expired' && (
                      <div className="lic-line warn">Call QPS if this looks wrong.</div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* The documents themselves live in the vault, which knows what has
              expired. Repeating them here would mean two screens that can
              disagree about whether the insurance is current. */}
          <button type="button" className="vp-link" onClick={() => setVendorView('docs')}>
            {t('lg.tabDocs')} <IconChevron size={15} />
          </button>
        </section>
      </div>

      {/* Appears the moment something changes and not before, so the page reads
          as a profile until it is being edited. */}
      {dirty && (
        <div className="vp-savebar">
          <button type="button" className="vp-save-cancel" disabled={isSubmittingApp}
            onClick={() => { seedVendorForm(); setDirty(false); }}>Undo changes</button>
          <button type="button" className="vp-save" disabled={isSubmittingApp} onClick={save}>
            {isSubmittingApp ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </>
  );
}

// The estimate's detail sheet used to live here: a summary card with the
// lines, the totals and a Create-Invoice button. It is gone because tapping a
// row now opens the document itself — letterhead, lines, totals — and two
// renderers for one estimate is two things that can disagree about what it
// says. Everything it did lives on the sheet: Invoice is an action on an
// approved one, and a tech still reads their own words back through
// localized() before the items reach the page.

// The working record behind an invoice's document: payments, progress reports,
// the tech's own uploaded file. Reached from Details on the sheet itself — the
// paper is what the invoice IS, this is everything that has happened to it.
function InvoiceSheet({ entry, onClose, onAddProgress }) {
  const { t } = useI18n();
  if (!entry) return null;
  return (
    <>
      <div className="modal active">
        <div className="modal-box">
          <div className="modal-handle"></div>
          <LedgerCard entry={entry} role="vendor" onAddProgress={onAddProgress} />
          <button className="btn btn-ghost btn-lg" style={{ marginTop: 14 }} onClick={onClose}>{t('req.cancel')}</button>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}

// Estimate status → the chip shown on a row. QPS approving a tech's price is
// what Joist calls Approved/Declined, so the words line up with the tabs.
function estimateChip(status, t) {
  if (status === 'approved') return { tone: 'approved', label: t('tp.approved') };
  if (status === 'rejected') return { tone: 'declined', label: t('tp.declined') };
  if (status === 'draft') return { tone: 'draft', label: t('tp.draft') };
  return { tone: 'pending', label: t('tp.pending') };
}

// Invoice money state → chip. Derived from what's actually been paid rather
// than the stored status, so a part-paid invoice always reads as part-paid.
function invoiceChip(entry, t) {
  const paid = paidTotal(entry);
  const amount = Number(entry.invoiceAmount) || 0;
  if (amount > 0 && paid >= amount) return { tone: 'paid', label: t('tp.paid') };
  if (paid > 0) return { tone: 'partial', label: t('tp.partial') };
  return { tone: 'pending', label: t('tp.unpaid') };
}

// Two letters off the business name for the header. A stock photograph would
// be a stranger's face on a tech's own account, and there is no avatar upload.
function initials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'QH';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export default function VendorDashboard() {
  const {
    currentScreen, goTo, currentUser, logout, vendorTab, setVendorTab, requests,
    setActiveRequest, setModal, vendorProfile,
    vendorView, setVendorView, ledger, openProgress,
    openCredential, handleDeleteCredential,
    projects, estimates, openEstimateForm, handleDeleteTechEstimate, openInvoiceForm,
    openEstimatePaper, openInvoicePaper, invoiceDetail, setInvoiceDetail,
  } = useApp();
  const { t, lang } = useI18n();
  const compliance = vendorProfile?.compliance;
  // Paperwork lapsed or lapsing. Read by the header bell and the tab-bar dot,
  // so the two can never disagree about whether something needs attention.
  const needsAttention = compliance?.status === 'blocked' || compliance?.status === 'expiring';

  // Stripe sends a tech back here after onboarding (?connect=done) or when the
  // link went stale (?connect=refresh). Either way they came from the Payments
  // screen, so land them back on it, and clear the query so a refresh or a
  // shared link doesn't keep re-triggering this.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect')) {
      setVendorView('payments');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sub-filters for the two segmented list screens.
  const [estSeg, setEstSeg] = useState('pending');
  const [invSeg, setInvSeg] = useState('unpaid');

  const jobsFor = (tab) => requests.filter(r => {
    if (tab === 'active') return ['assigned', 'in-progress'].includes(r.status);
    if (tab === 'completed') return ['completed', 'invoiced'].includes(r.status);
    return r.status === 'paid';
  });
  const jobs = jobsFor(vendorTab);

  // ── FREE TO USE, PAID TO BE SENT WORK ─────────────────────────────────
  //
  // Julian's rule, in his own words: the estimates and invoices a
  // technician writes for their OWN customers are completely free and need
  // no Stripe. Stripe is only needed when QPS Hub sends them a lead, so
  // the money for that job can reach their bank.
  //
  // So nothing here blocks writing or sending a document. This is the one
  // place the requirement bites, and only once there is QPS work on their
  // screen to be paid for.
  const qpsWork = requests.filter(r => ['assigned', 'in-progress', 'completed', 'invoiced'].includes(r.status));
  const needsStripeForQps = qpsWork.length > 0 && !vendorProfile?.chargesEnabled;

  // ── Rows for the three list screens ──────────────────────────────────────


  const estimateRows = useMemo(() => (estimates || []).map(e => {
    const trade = localized(e, 'trade', lang);
    return {
    id: e._id,
    // Whose job it is, in the big type. This read the service address, and
    // when that was empty it read the trade — so an estimate for Olivia came
    // up on the list as the single word "Work". A technician scanning this
    // screen is looking for a person.
    name: docWho(e) || e.serviceAddress || trade || t('tp.untitled'),
    subtitle: docAbout(e, trade, lang),
    number: e.estimateNumber,
    date: e.createdAt || e.submittedAt,
    amount: e.techTotal,
    status: e.status,
    ...(({ tone, label }) => ({ chipTone: tone, chipLabel: label }))(estimateChip(e.status, t)),
    // Straight to the document, whatever state it is in. A summary card was
    // one screen between a tech and the thing they actually wanted to read.
    // Edit is on the sheet for a draft, and nowhere for anything sent.
    onClick: () => openEstimatePaper(e),
    // Slide the row aside to reveal Delete. An approved estimate is already a
    // line on a project statement, so the server refuses it and says why.
    onDelete: e.status === 'approved' ? null : () => handleDeleteTechEstimate(e),
    };
  }), [estimates, t, lang, openEstimatePaper, handleDeleteTechEstimate]);

  const invoiceRows = useMemo(() => (ledger || []).map(e => ({
    id: e._id,
    // Same as the estimates list above: the person first, the work second.
    name: docWho(e) || e.serviceAddress || localized(e, 'jobDetails', lang) || t('tp.untitled'),
    subtitle: docAbout(e, localized(e, 'jobDetails', lang), lang),
    meta: '',
    number: e.invoiceNumber,
    date: e.createdAt,
    amount: e.invoiceAmount,
    entry: e,
    ...(({ tone, label }) => ({ chipTone: tone, chipLabel: label }))(invoiceChip(e, t)),
    onClick: () => openInvoicePaper(e),
  })), [ledger, t, lang, openInvoicePaper]);

  // Every payment across every invoice, flattened into one feed.
  const paymentRows = useMemo(() => (ledger || []).flatMap(e =>
    (e.payments || []).map(p => ({
      id: `${e._id}-${p._id}`,
      name: e.serviceAddress || localized(e, 'jobDetails', lang) || t('tp.untitled'),
      meta: [p.mode, p.reference].filter(Boolean).join(' · '),
      number: e.invoiceNumber,
      date: p.date || p.recordedAt,
      amount: p.amount,
      chipTone: 'paid',
      chipLabel: t('tp.received'),
      onClick: () => openInvoicePaper(e),
    }))
  ), [ledger, t, lang, openInvoicePaper]);

  const totals = ledgerTotals(ledger || []);

  const TABS = [
    { key: 'home', label: t('tp.overview', 'Overview'), Icon: IconHome },
    { key: 'jobs', label: t('tp.jobs'), Icon: IconJobs },
    { key: 'estimates', label: t('tp.estimates'), Icon: IconEstimates },
    { key: 'invoices', label: t('tp.invoices'), Icon: IconInvoices },
    { key: 'payments', label: t('tp.payments'), Icon: IconPayments },
    { key: 'more', label: t('tp.more'), Icon: IconMore, dot: needsAttention },
  ];

  return (
    <div className={`screen ${currentScreen === 'screen-vendor-dashboard' ? 'active' : ''}`}>
      {/* The dark bar this replaces carried a seal, a camel pill reading "Tech"
          and the signed-in name in --border grey, which measures 1.9:1 on the
          forest green — a name you cannot read is not a greeting. */}
      <header className="qps-portal-header">
        <div className="qps-header-left">
          <button type="button" className="qps-brand-logo" onClick={() => goTo('screen-home')} aria-label="QPS Hub home">
            <Wordmark size={24} />
          </button>
        </div>

        <div className="qps-header-right">
          <LanguageSwitcher />
          <ThemeToggle />

          {/* The bell is a real control, not decoration: it opens the screen
              where push notifications are switched on, and carries the same
              red dot the tab bar uses when paperwork needs attention. A bell
              that opens nothing teaches a tech to stop tapping things. */}
          <button
            type="button"
            className="qps-nav-icon"
            aria-label={needsAttention ? 'Your paperwork needs attention' : 'Notification settings'}
            title={needsAttention ? 'Your paperwork needs attention' : 'Notification settings'}
            onClick={() => setVendorView(needsAttention ? 'docs' : 'more')}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 8.5a6 6 0 10-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5" />
              <path d="M13.7 19.5a2 2 0 01-3.4 0" />
            </svg>
            {needsAttention && <span className="qps-nav-dot" />}
          </button>

          {/* Initials, not a photograph. There is no avatar upload, so a stock
              face would be a stranger's picture on a tech's own account.
              Tapping it opens the profile — it is the thing on the screen that
              most looks like "you", so it is where a hand goes looking. */}
          <button type="button"
            className={`qps-user-avatar as-button${vendorProfile?.profile?.logoUrl ? ' has-img' : ''}`}
            style={vendorProfile?.profile?.logoUrl ? { backgroundImage: `url(${vendorProfile.profile.logoUrl})` } : undefined}
            onClick={() => setVendorView('profile')}
            aria-label={t('tp.profile')} title={t('tp.profile')}>
            {!vendorProfile?.profile?.logoUrl && initials(vendorProfile?.businessName || currentUser?.name)}
          </button>

          <button type="button" className="qps-logout-icon" aria-label={t('nav.logout')} title={t('nav.logout')} onClick={logout}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 4.5h3.5A1.5 1.5 0 0120 6v12a1.5 1.5 0 01-1.5 1.5H15" />
              <path d="M10.5 15.5L14 12l-3.5-3.5M14 12H4" />
            </svg>
          </button>
        </div>
      </header>

      {/* The application used to REPLACE the portal until it was finished, so a
          technician who signed up at nine at night and stopped halfway had no
          way to see what he had joined. Now it is one screen among the others
          and the note below points at it. Nothing was unlocked by this: being
          sent work and being paid are both gated on paperwork, server-side,
          and neither has ever looked at whether the wizard was finished. */}
      {vendorView === 'apply' ? (
        <VendorApplication editing onClose={() => setVendorView('home')} />
      ) : (
      <>
      <div className="tp-body">
        {/* On every screen until there is nothing left to chase. */}
        <div className="vsn-wrap"><VendorSetupNote /></div>

        {/* ── Overview ─────────────────────────────────────────── */}
        {/* Signing in used to drop a tech straight into a list of jobs, which
            on day one is empty. This is the screen that answers "what is my
            standing here" before it answers "what is on today". */}
        {vendorView === 'home' && <VendorOverview />}

        {/* ── Jobs ─────────────────────────────────────────────── */}
        {vendorView === 'jobs' && (
          <>
            {/* QPS sent them work and has nowhere to send the money. Said
                here, on the jobs themselves, rather than left for them to
                find on a tab they have no reason to open. */}
            {needsStripeForQps && (
              <div className="tp-stripe-band">
                <div className="tp-stripe-t">
                  {qpsWork.length === 1 ? 'QPS Hub sent you a job' : `QPS Hub sent you ${qpsWork.length} jobs`}
                </div>
                <div className="tp-stripe-s">
                  Connect your bank with Stripe so the money for {qpsWork.length === 1 ? 'it' : 'them'} can
                  reach you. It takes about five minutes and there is no monthly fee.
                </div>
                <div className="tp-stripe-s" style={{ marginTop: 6 }}>
                  Your own estimates and invoices keep working either way — those are free and always will be.
                </div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
                  onClick={() => setVendorView('payments')}>Set up getting paid</button>
              </div>
            )}

            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">{t('tp.jobs')}</div>
                <div style={{ width: 34 }} />
              </div>
              <div className="tp-seg" role="tablist">
                {['active', 'completed', 'paid'].map(tab => (
                  <button key={tab} role="tab" aria-selected={vendorTab === tab}
                    className={vendorTab === tab ? 'active' : ''} onClick={() => setVendorTab(tab)}>
                    {t('vd.' + tab)}
                    {jobsFor(tab).length > 0 && <span className="tp-seg-n">{jobsFor(tab).length}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: '16px 16px 0' }}>
              {/* The compliance banner used to sit here as well as in the note
                  above, so a technician missing his W-9 read the same thing
                  twice in red before reaching anything he could do. It lives
                  on the documents screen now, where the detail belongs. */}
              {/* Anything offered and not yet answered sits above the list, not
                  in it. An offer is a question; the list below is work he
                  already has. */}
              <JobOffers />
              {jobs.map(req => (
                <div key={req._id} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                    <div style={{ fontSize: 15 }}>{tr('opt.' + req.serviceType, req.serviceType)}</div>
                    {statusTag(req.status)}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>{req.homeownerId?.name || req.fullName}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{req.serviceAddress}</div>
                  {/* A commercial job carries things a homeowner job never
                      does, and all of them decide whether this visit works:
                      whether it's an emergency, who to ring to be let in, and
                      the move-out inspection the work is being quoted against.
                      A document nobody on site can open is the same as no
                      document, which is why it is HERE and not only in the
                      manager's portal. */}
                  {req.priority && req.priority !== 'routine' && (
                    <div style={{ marginBottom: 8 }}>
                      <span className={`tag tag-${req.priority === 'emergency' ? 'red' : 'amber'}`}>
                        {tr('acct.priority.' + req.priority, req.priority)}
                      </span>
                    </div>
                  )}
                  {(req.access?.tenantName || req.access?.lockboxCode || req.access?.gateCode || req.access?.pets) && (
                    <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', marginBottom: 8, fontSize: 12.5, lineHeight: 1.7 }}>
                      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 2 }}>
                        {tr('acct.gettingIn', 'Getting in')}
                      </div>
                      {req.access.tenantName && (
                        <div>
                          {req.access.tenantName}
                          {req.access.tenantPhone && <> · <a href={`tel:${req.access.tenantPhone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{req.access.tenantPhone}</a></>}
                        </div>
                      )}
                      {req.access.lockboxCode && <div>{tr('acct.lockboxCode', 'Lockbox')}: {req.access.lockboxCode}</div>}
                      {req.access.gateCode && <div>{tr('acct.gateCode', 'Gate')}: {req.access.gateCode}</div>}
                      {req.access.pets && <div>{tr('acct.pets', 'Pets')}: {req.access.pets}</div>}
                      {req.access.notes && <div>{req.access.notes}</div>}
                    </div>
                  )}
                  {(req.documents || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      {req.documents.map((d, i) => (
                        <a key={d._id || i} className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          {d.name || tr('doc.file', 'File')}
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Everything the customer asked for, not just the trade the
                      job is filed under. A visit booked as "Plumbing" that is
                      really a leak, a gate and a hole in the wall is three jobs
                      the tech should know about before they load the van. */}
                  {req.items?.length > 1 && (
                    <div style={{ marginBottom: 8 }}>
                      {req.items.map((it, i) => (
                        <div key={i} style={{ fontSize: 13, color: 'var(--slate)', padding: '2px 0' }}>
                          <span style={{ color: 'var(--muted)' }}>{i + 1}. </span>
                          {it.title || tr('opt.' + it.serviceType, it.serviceType)}
                          {it.photos?.length > 0 && <span style={{ color: 'var(--muted)' }}> · {it.photos.length}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {req.estimate && <div style={{ fontSize: 13, color: 'var(--green)', marginBottom: 10 }}>{t('vd.estimate')} {fmtMoney(req.estimate.amount)}</div>}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <MessageButton request={req} />
                    {/* Priced from here, not from a blank form: the address,
                        the trade and the property photo come with the job.
                        Gone once the work is billed: an estimate written after
                        the invoice cannot change what the customer owes, and
                        offering it on a finished job reads like the invoice
                        did not take. */}
                    {req.invoice?.amount == null && (
                      <button className="btn btn-outline btn-sm" onClick={() => openEstimateForm(req)}>
                        {t('pj.newEstimate')}
                      </button>
                    )}
                    {req.status === 'assigned' && (
                      <button className="btn btn-primary btn-sm" onClick={() => { setActiveRequest(req); setModal('completion'); }}>{t('vd.submitCompletionPhotos')}</button>
                    )}
                    {req.status === 'completed' && (
                      <button className="btn btn-green btn-sm" onClick={() => { setActiveRequest(req); setModal('invoice'); }}>{t('vd.submitInvoice')}</button>
                    )}
                    {req.status === 'invoiced' && (
                      /* This said "Invoice pending admin approval", which is not
                         true and sent Julian looking for a button that does not
                         exist. Admin approval records that QPS agrees to pay the
                         technician. It has never gated the customer: the bill
                         emails the moment it is raised and the pay link works
                         straight away. */
                      <span style={{ fontSize: 12, color: 'var(--green)' }}>{t('vd.invoiceSent')}</span>
                    )}
                    {req.status === 'paid' && (
                      <span style={{ fontSize: 12, color: 'var(--green)' }}>{t('vd.paidDash')} {fmtMoney(req.invoice?.amount)}</span>
                    )}
                  </div>
                </div>
              ))}
              {jobs.length === 0 && (
                <div className="tp-empty">
                  <div className="tp-empty-t">{t('tp.noJobsT')}</div>
                  <div>{t('vd.noJobs')}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Estimates ────────────────────────────────────────── */}
        {vendorView === 'estimates' && (
          <RecordList
            title={t('tp.estimates')}
            rows={estimateRows}
            segments={[
              { key: 'pending', label: t('tp.pending'), match: r => r.status === 'submitted' || r.status === 'draft' },
              { key: 'approved', label: t('tp.approved'), match: r => r.status === 'approved' },
              { key: 'declined', label: t('tp.declined'), match: r => r.status === 'rejected' },
            ]}
            segment={estSeg}
            onSegment={setEstSeg}
            searchPlaceholder={t('tp.searchEstimates')}
            onAdd={() => openEstimateForm()}
            addLabel={t('pj.newEstimate')}
            emptyTitle={t('tp.noEstimates')}
            emptyText={t('tp.noEstimatesSub')}
          />
        )}

        {/* ── Invoices ─────────────────────────────────────────── */}
        {vendorView === 'invoices' && (
          <>
            <RecordList
              title={t('tp.invoices')}
              rows={invoiceRows}
              segments={[
                { key: 'unpaid', label: t('tp.unpaid'), match: r => paidTotal(r.entry) === 0 },
                { key: 'partial', label: t('tp.partial'), match: r => paidTotal(r.entry) > 0 && balanceOf(r.entry) > 0 },
                { key: 'paid', label: t('tp.paid'), match: r => (Number(r.entry.invoiceAmount) || 0) > 0 && balanceOf(r.entry) <= 0 },
              ]}
              segment={invSeg}
              onSegment={setInvSeg}
              searchPlaceholder={t('tp.searchInvoices')}
              onAdd={() => openInvoiceForm()}
              addLabel={t('lg.submitInvoice')}
              totals={[
                [t('lg.totalInvoiced'), totals.invoiced],
                [t('lg.totalPaid'), totals.paid],
                [t('lg.totalOwed'), totals.owed],
              ]}
              emptyTitle={t('tp.noInvoicesT')}
              emptyText={t('tp.noInvoicesSub')}
            />
          </>
        )}

        {/* ── Payments ─────────────────────────────────────────── */}
        {vendorView === 'payments' && <OnlinePayments />}
        {vendorView === 'payments' && (
          <RecordList
            title={t('tp.payments')}
            rows={paymentRows}
            searchPlaceholder={t('tp.searchPayments')}
            totals={[
              [t('lg.totalPaid'), totals.paid],
              [t('lg.totalOwed'), totals.owed],
            ]}
            emptyTitle={t('tp.noPayments')}
            emptyText={t('tp.noPaymentsSub')}
          />
        )}

        {/* ── More ─────────────────────────────────────────────── */}
        {vendorView === 'more' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">{t('tp.more')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>

            <div style={{ padding: '0 14px' }}><InstallCard /></div>

            <div className="tp-more-g">
              <div className="tp-more-h">{t('tp.workGroup')}</div>
              <button className="tp-more-row" onClick={() => setVendorView('docs')}>
                <span>
                  {t('lg.tabDocs')}
                  {compliance?.status === 'blocked' && <span style={{ color: 'var(--red)' }}> ●</span>}
                  {compliance?.status === 'expiring' && <span style={{ color: 'var(--amber)' }}> ●</span>}
                </span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
              <button className="tp-more-row" onClick={() => setVendorView('settlements')}>
                <span>{t('tp.settlements')}</span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
            </div>

            <div className="tp-more-g">
              <div className="tp-more-h">{t('tp.account')}</div>
              <button className="tp-more-row" onClick={() => setVendorView('profile')}>
                <span>{t('tp.profile')}
                  <span style={{ color: 'var(--muted)', fontSize: 13, display: 'block', marginTop: 2 }}>
                    {vendorProfile?.businessName || currentUser?.name}
                  </span>
                </span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
              {/* A technician could not change their own password anywhere in
                  this app. The only route was Forgot Password, which sends an
                  email — the exact thing that was not arriving. The API has
                  always been there; only the admin header ever called it. */}
              <button className="tp-more-row" onClick={() => setModal('password')}>
                <span>{t('acct.password')}</span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
              <button className="tp-more-row" onClick={() => setModal('email')}>
                <span>{t('acct.email')}
                  <span style={{ color: 'var(--muted)', fontSize: 13, display: 'block', marginTop: 2 }}>
                    {currentUser?.email}
                  </span>
                </span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
              <div className="tp-more-row" style={{ cursor: 'default' }}>
                <span>{t('tp.language')}</span>
                <LanguageSwitcher />
              </div>
              <button className="tp-more-row" onClick={logout}>
                <span style={{ color: 'var(--red)' }}>{t('nav.logout')}</span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
            </div>
          </>
        )}

        {/* ── Profile ──────────────────────────────────────────── */}
        {/* The wizard is no longer reachable from here. It is the first-run
            application and nothing else; everything it asks is on the profile,
            editable where it is read. */}
        {vendorView === 'profile' && <VendorProfile />}

        {/* ── My Documents (reached from More) ─────────────────── */}
        {vendorView === 'docs' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <button className="tp-act" onClick={() => setVendorView('more')} aria-label={t('nav.back')}
                  style={{ transform: 'scaleX(-1)' }}><IconChevron size={20} /></button>
                <div className="tp-title">{t('lg.tabDocs')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>
            <div style={{ padding: '16px 16px 0' }}>
              <ComplianceBanner compliance={compliance} forVendor />

              {/* The two things that decide whether a technician can be paid,
                  above the upload list rather than inside it. Both used to
                  happen by text message: a photo of a voided cheque, and a
                  W-9 somebody had to chase. */}
              <W9Form />
              <PayoutDetails />

              <div style={{ fontSize: 13, color: 'var(--muted)', margin: '20px 0 14px', lineHeight: 1.5 }}>{t('lg.docsIntro')}</div>
              <CompliancePanel
                compliance={compliance}
                onUpload={(item) => openCredential(item)}
                onDelete={(item) => handleDeleteCredential(item)}
              />
            </div>
          </>
        )}

        {/* ── Project settlements (reached from More) ──────────── */}
        {vendorView === 'settlements' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <button className="tp-act" onClick={() => setVendorView('more')} aria-label={t('nav.back')}
                  style={{ transform: 'scaleX(-1)' }}><IconChevron size={20} /></button>
                <div className="tp-title">{t('tp.settlements')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>
            <div style={{ padding: '16px 16px 0' }}>
              {(projects || []).length === 0 ? (
                <div className="tp-empty">
                  <div className="tp-empty-t">{t('tp.settlements')}</div>
                  <div>{t('pj.noSettlements')}</div>
                </div>
              ) : projects.map(s2 => <TechSettlement key={s2.projectId} settlement={s2} />)}
            </div>
          </>
        )}
      </div>

      {/* ── Bottom tab bar ─────────────────────────────────────── */}
      <div className="tp-bar" role="tablist">
        {TABS.map(({ key, label, Icon, dot }) => {
          // "Documents" and "Settlements" are pushed from More, so More stays
          // lit while the tech is inside either of them.
          const active = vendorView === key
            || (key === 'more' && ['docs', 'settlements'].includes(vendorView));
          return (
            <button key={key} role="tab" aria-selected={active} className={active ? 'active' : ''}
              onClick={() => setVendorView(key)}>
              <Icon size={21} />
              <span className="tp-bar-l">{label}</span>
              {dot && <span className="tp-bar-dot" />}
            </button>
          );
        })}
      </div>

      {/* The working record behind an invoice's document, reached from the
          Details action on the sheet itself. */}
      <InvoiceSheet entry={invoiceDetail} onClose={() => setInvoiceDetail(null)} onAddProgress={openProgress} />
      </>
      )}
    </div>
  );
}
